/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable import/no-named-as-default-member */
import * as Sentry from '@sentry/browser';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';

import { useAppState } from '../../AppStateProvider';
import type { SettingsAppStorage } from '../../domain/settings';
import type { User } from '../../domain/user';
import { AppcuesService } from '../../services/appcues';
import { AppMode, appStore } from '../../services/appStore';
import { getBuildNumber, getExtensionVersion } from '../../services/browser';
import * as Fullstory from '../../services/fullstory';
import { parseStringifiedValues } from '../../services/string';
import { growthBook } from '../../services/tracking/growthbook';
import { matomoTracker } from '../../services/tracking/matomoTracker';
import UrlService from '../../services/UrlService';
import { setSettingsItems } from '../../services/windowMessaging';

import { userApi } from './api';
import { postAuthStatus, postEmailAgentFeatureStatus } from './userNotifications';
import userQueryKey from './userQueryKey';

const initializeServices = (user: User) => {
  if (user.isExtensionEnabled) {
    Fullstory.init();
    Fullstory.identify(user);
  }
  mixpanel.identify(`${user.userId}`);

  const userProperties = {
    email: user.email,
    user_id: user.userId,
    account_id: user.accountId,
    base_product: user.accountType,
  };
  mixpanel.register(userProperties);
  matomoTracker.setUserProperties(userProperties);
  growthBook.setUserProperties({ id: user.userId, email: user.email });
  AppcuesService.init(user);
  Sentry.setTags({ ...userProperties, extension_version: getExtensionVersion() });
};

const setUiVersion = (user: User, settingsStorage: SettingsAppStorage, mode: AppMode) => {
  const currentUiVersion = getBuildNumber();

  // if you need to set a custom UI version, you must use this condition
  // if (!settingsStorage.isCustomUiVersion && !!currentUiVersion)
  if ((!settingsStorage.isCustomUiVersion || mode === AppMode.ProSidebar) && !!currentUiVersion) {
    if (currentUiVersion !== user.uiVersion) {
      window.parent.postMessage({ message: 'set uiversion', uiVersion: user.uiVersion }, '*');
    } else {
      setSettingsItems({ uiVersion: user.uiVersion });
    }
  }
};

const handleFirstOpenedAfterPurchase = () => {
  setSettingsItems({ isFirstOpenedAfterPurchase: true });
  appStore.setSettingsStorageValue({ isFirstOpenedAfterPurchase: true });
  window.parent.postMessage({ message: 'open' }, '*');
};

const handleImpersonateUser = () => {
  mixpanel.disable();
  matomoTracker.disable();
  Sentry.close(0).catch(console.error);
};

export const useUserQuery = (): UseQueryResult<User> => {
  const { isOpen, settingsStorage, url, mode } = useAppState();
  const isProPage = !!url && UrlService.isProPage(new URL(url));
  const isGoogleCalendarPage = !!url && UrlService.isGoogleCalendarUrl(new URL(url));
  const isGmailPage = !!url && UrlService.isGmailUrl(new URL(url));

  const query = useQuery({
    queryKey: userQueryKey.userInfo(),
    queryFn: async ({ signal }) => {
      const user = await userApi.getUser(signal);

      postAuthStatus(!!user);

      if (user) {
        postEmailAgentFeatureStatus(user);
        if (user.userSettings) {
          Object.assign(user, { userSettings: parseStringifiedValues(user.userSettings) });
        }

        setUiVersion(user, settingsStorage, mode);

        if (user.isNoTouchAccount && isProPage && !settingsStorage.isFirstOpenedAfterPurchase) {
          handleFirstOpenedAfterPurchase();
        }

        if (user.isImpersonate) {
          handleImpersonateUser();
          return user;
        }

        initializeServices(user);
      } else {
        mixpanel.reset();
      }
      return user;
    },
    enabled: isOpen || isProPage || isGoogleCalendarPage || isGmailPage,
  });

  if (query.error) {
    postAuthStatus(false);
  }

  return query;
};
